// ═══════════════════════════════════════════════════════════════════════════
// postventaLegacyShape — reclamo/devolución CANÓNICOS → shape legacy
//                        mlReclamos / mlDevoluciones
// ───────────────────────────────────────────────────────────────────────────
// W3 tajada 6 (D6 del PLAN_canonizar-postventa) — mapeo de borde único para
// los consumidores del shape legacy (PostventaDashboard + clasificador de
// agentes vía interceptor 7.6). Mismo patrón que publicacionesLegacyShape /
// ordenParaCascada. Se borra junto con el refactor a camelCase nativo del
// front (post-retiro legacy, W3 tajada 7+).
//
// Fuente del shape canónico: reclamosShape.js / devolucionesShape.js +
// reclamosRepo (núcleo APLANADO al top-level del doc, datosCanal anidado) +
// reclamos-enriquecer canónico (datosCanal.enriquecimiento = mismo mapper
// que el `_enriched` legacy, REUSADO por require — identidad por construcción,
// salvo `completadoEn` que acá se re-expone como `completedAt`).
//
// DIVERGENCIAS declaradas vs doc legacy mlReclamos:
//   - `claimedQuantity` / `availableActions` / `claimVersion`: NO existen en
//     la canónica (el legacy los pescaba con un fetchClaim extra en el sync;
//     el sync canónico no hace detail-fetch — la resolución viene inline).
//     La UI ya muestra '—' ante null.
//   - `title`/`description`/`problem`/`dueDate`/`actionResponsible` a nivel
//     doc: viven SOLO en `_enriched.detail` (la UI ya cae ahí como primera
//     fuente; el doc-level legacy era el fallback para claims sin enrich).
//   - Solo claims donde la empresa es VENDEDOR (esReclamoComoVendedor, PR #64)
//     — los claims-compra que el legacy persistía ya no entran al universo.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Reclamo canónico (doc de `reclamos`, núcleo aplanado) → shape legacy mlReclamos.
 * Campos auditados contra los lectores reales (12-jun): PostventaDashboard
 * (tablas/modal/KPIs), clasificadores/postventa.js (itemIdDeReclamo,
 * reclamoAbierto, buildDevolucionesPorPub) y useOrdenesPostventa (cascada).
 */
export function reclamoCanonicoALegacy(r) {
  const dc = r?.datosCanal || {}
  const e = dc.enriquecimiento || null
  const res = r?.resolucion || null
  return {
    // El doc legacy usaba claimId como docId — el front lo usa de key/join.
    id: r.externalId ?? r.id,
    _docId: r._docId ?? r.id ?? null,   // docId canónico real (debug/trazas)
    claimId: r.externalId ?? null,
    type: dc.type ?? null,              // 'mediations' | 'returns' | ...
    status: dc.status ?? null,          // raw ML: 'opened' | 'closed'
    stage: dc.stage ?? null,            // raw ML: 'claim' | 'dispute' | ...
    fulfilled: r.cumplido === true,
    reasonId: r.motivoId ?? null,
    dateCreated: r.fechaCreada ?? null,
    lastUpdated: r.fechaModifCanal ?? null,
    resource: dc.resourceTipo ?? null,  // 'order' | 'shipment' (tipo, no path)
    resourceId: dc.resourceId ?? null,
    orderId: r.refCanalOrden ?? null,
    siteId: dc.siteId ?? null,
    parentId: dc.parentId ?? null,
    quantityType: dc.quantityType ?? null,
    players: Array.isArray(dc.players) ? dc.players : [],
    // Resolución — inline en la canónica (núcleo), nombres legacy.
    resolutionReason: res?.motivo ?? null,
    resolutionDateCreated: res?.fecha ?? null,
    resolutionClosedBy: res?.cerradoPor ?? null,
    resolutionBenefited: res?.beneficiados ?? null,
    resolutionAppliedCoverage: res?.coberturaAplicada ?? null,
    // itemId post-enrich (vía 1 de itemIdDeReclamo — el camino canónico).
    itemId: e?.itemId != null ? String(e.itemId) : null,
    itemTitle: e?.itemTitle ?? null,
    // _enriched: mismo shape que el legacy (mapper reusado). El front lee
    // `completedAt`; el enricher canónico persiste `completadoEn`.
    _enriched: e ? { ...e, completedAt: e.completadoEn ?? null } : null,
  }
}

/**
 * Unifica reclamos tipo return + devoluciones enriquecidas en UN incidente
 * por claim (decisión Mariano 12-jun: una sola tabla enriquecida).
 *
 * El conteo legacy "returns claims + devoluciones" contaba DOBLE cuando el
 * claim tenía su return enriquecido (mismo incidente, 2 filas). Acá: clave =
 * claimId; si hay return doc se usa como fila principal con el reclamo de
 * contexto; los claims tipo return SIN return doc entran solos (sinReturn).
 * Las mediations con return doc también entran (la devolución manda).
 *
 * Opera sobre shapes LEGACY (post-espejo). Devuelve [{ reclamo, devolucion }]
 * ordenado por fecha desc.
 */
export function unificarIncidentesDevolucion(reclamos = [], devoluciones = []) {
  const reclamosPorClaimId = {}
  for (const r of reclamos) {
    if (r?.claimId != null) reclamosPorClaimId[String(r.claimId)] = r
  }
  const porClaim = new Map()
  for (const d of devoluciones) {
    if (d?.claimId == null) continue
    porClaim.set(String(d.claimId), {
      devolucion: d,
      reclamo: reclamosPorClaimId[String(d.claimId)] || null,
    })
  }
  for (const r of reclamos) {
    const esReturn = r?.type === 'returns' || r?.type === 'return'
    if (!esReturn || r?.claimId == null) continue
    if (!porClaim.has(String(r.claimId))) {
      porClaim.set(String(r.claimId), { devolucion: null, reclamo: r })
    }
  }
  const fechaDe = (inc) =>
    new Date(inc.devolucion?.dateCreated || inc.reclamo?.dateCreated || 0).getTime()
  return [...porClaim.values()].sort((a, b) => fechaDe(b) - fechaDe(a))
}

/**
 * Devolución canónica (doc de `devoluciones`, núcleo aplanado) → shape legacy
 * mlDevoluciones. `estado` canónico = status raw de ML (fail-honest hasta
 * censar el enum real de returns) → pasa directo a `status`.
 */
export function devolucionCanonicaALegacy(d) {
  return {
    id: d.externalId ?? d.id,
    _docId: d._docId ?? d.id ?? null,
    returnId: d.externalId ?? null,
    claimId: d.reclamoId ?? null,
    status: d.estado ?? null,
    subtype: d.subtipo ?? null,
    returnReason: d.motivo ?? null,
    refundStatus: d.estadoReembolso ?? null,
    dateCreated: d.fechaCreada ?? null,
    dateClosed: d.fechaCierre ?? null,
    lastUpdated: d.fechaModifCanal ?? null,
  }
}
